/**
 * Assign event courts to matches within a single round without double-booking.
 * The first N matches receive the first N reserved courts; overflow matches stay unassigned.
 */
export function resolvePlayEventMatchCourtIds(
  eventCourtIds: string[],
  matchCount: number
): Array<string | null> {
  return Array.from({ length: matchCount }, (_, index) => eventCourtIds[index] ?? null);
}

export interface DrawPreviewCourtAssignmentRound {
  roundNumber: number;
  matches: Array<{ matchIndex: number; isBye?: boolean | null }>;
}

/** Auto-assign reserved courts to preview matches; overflow matches stay unassigned (TBD). */
export function buildAutoCourtAssignmentsByMatchKey(
  eventCourtIds: string[],
  rounds: DrawPreviewCourtAssignmentRound[]
): Record<string, string | null> {
  const assignments: Record<string, string | null> = {};

  for (const round of rounds) {
    const sortedMatches = [...round.matches].sort((a, b) => a.matchIndex - b.matchIndex);
    const playableCount = sortedMatches.filter((match) => !match.isBye).length;
    const autoCourts = resolvePlayEventMatchCourtIds(eventCourtIds, playableCount);
    let playableIndex = 0;

    sortedMatches.forEach((match, indexInRound) => {
      const key = `${round.roundNumber}-${indexInRound}`;
      if (match.isBye) {
        assignments[key] = null;
        return;
      }
      assignments[key] = autoCourts[playableIndex] ?? null;
      playableIndex += 1;
    });
  }

  return assignments;
}

/** Courts from the event that are not already assigned to another match in the same round. */
export function listAvailablePlayEventCourtsForRound(
  eventCourtIds: string[],
  roundMatches: Array<{ id: string; courtId?: string | null }>,
  matchId: string
): string[] {
  const usedCourtIds = new Set(
    roundMatches
      .filter((match) => match.id !== matchId && match.courtId)
      .map((match) => match.courtId as string)
  );
  return eventCourtIds.filter((courtId) => !usedCourtIds.has(courtId));
}
